import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../lib/api.ts';
import { useConfirm } from '../lib/confirm.tsx';
import { Btn } from '../components/Btn.tsx';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import { Modal } from '../components/Modal.tsx';
import { Field, TextArea, TextInput } from '../components/Field.tsx';
import { ErrorMessage } from '../components/ErrorMessage.tsx';
import type { DocumentType } from '../lib/types.ts';

type FieldDraft = { field_key: string; label: string; description: string };

function slugify(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 120);
}

export function AdminDocumentTypes() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const { data, isLoading } = useQuery({
    queryKey: ['document-types'],
    queryFn: () => adminApi.get<{ document_types: DocumentType[] }>('/api/document-types'),
  });

  const [editing, setEditing] = useState<DocumentType | null>(null);
  const [creating, setCreating] = useState(false);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.delete(`/api/document-types/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['document-types'] }),
  });

  if (isLoading) return <CenteredSpinner />;
  const types = data?.document_types ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Document types</h1>
          <p className="mt-1 text-sm text-slate-500">
            Define the kinds of document you collect and the fields AI should verify exist on
            upload. Link a type to an upload question in the question builder. High-confidence
            uploads pass automatically; low-confidence ones are sent to the verification queue.
          </p>
        </div>
        <Btn onClick={() => setCreating(true)}>+ New document type</Btn>
      </div>

      {types.length === 0 ? (
        <EmptyState
          title="No document types yet"
          description="Create a document type (e.g. Public Liability Certificate) and the fields to verify."
          action={<Btn onClick={() => setCreating(true)}>+ New document type</Btn>}
        />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {types.map((t) => (
            <div key={t.id} className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-slate-900">{t.name}</span>
                    {!t.is_active && (
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                        inactive
                      </span>
                    )}
                  </div>
                  {t.description && <div className="mt-0.5 text-xs text-slate-500">{t.description}</div>}
                </div>
                <div className="flex shrink-0 gap-2">
                  <button className="text-xs text-slate-600 hover:underline" onClick={() => setEditing(t)}>
                    Edit
                  </button>
                  <button
                    className="text-xs text-red-600 hover:underline"
                    onClick={() => {
                      void (async () => {
                        if (
                          await confirm({
                            title: 'Delete document type',
                            body: <>Delete <span className="font-semibold">{t.name}</span>?</>,
                            confirmLabel: 'Delete',
                            danger: true,
                          })
                        )
                          deleteMutation.mutate(t.id);
                      })();
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-1">
                {t.document_type_fields.length === 0 ? (
                  <span className="text-xs italic text-slate-400">No fields to verify</span>
                ) : (
                  t.document_type_fields.map((f) => (
                    <span
                      key={f.id}
                      className="rounded-full bg-blue-50 px-2 py-0.5 text-xs text-blue-700"
                    >
                      {f.label}
                    </span>
                  ))
                )}
              </div>
              <div className="mt-3 text-xs text-slate-500">
                Auto-pass at {Math.round(t.auto_pass_threshold * 100)}% confidence
              </div>
            </div>
          ))}
        </div>
      )}

      {deleteMutation.error && <ErrorMessage error={deleteMutation.error} />}

      {creating && <DocumentTypeEditor mode="create" onClose={() => setCreating(false)} />}
      {editing && (
        <DocumentTypeEditor mode="edit" existing={editing} onClose={() => setEditing(null)} />
      )}
    </div>
  );
}

function DocumentTypeEditor({
  mode,
  existing,
  onClose,
}: {
  mode: 'create' | 'edit';
  existing?: DocumentType;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState(existing?.name ?? '');
  const [description, setDescription] = useState(existing?.description ?? '');
  const [threshold, setThreshold] = useState(
    Math.round((existing?.auto_pass_threshold ?? 0.8) * 100),
  );
  const [isActive, setIsActive] = useState(existing?.is_active ?? true);
  const [fields, setFields] = useState<FieldDraft[]>(
    existing?.document_type_fields.map((f) => ({
      field_key: f.field_key,
      label: f.label,
      description: f.description ?? '',
    })) ?? [],
  );
  const [error, setError] = useState<unknown>(null);

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        name: name.trim(),
        description: description.trim() || null,
        auto_pass_threshold: threshold / 100,
        is_active: isActive,
        fields: fields
          .filter((f) => f.label.trim())
          .map((f) => ({
            field_key: f.field_key.trim() || slugify(f.label),
            label: f.label.trim(),
            description: f.description.trim() || null,
          })),
      };
      return mode === 'create'
        ? adminApi.post('/api/document-types', payload)
        : adminApi.patch(`/api/document-types/${existing!.id}`, payload);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['document-types'] });
      onClose();
    },
    onError: setError,
  });

  function addField() {
    setFields((f) => [...f, { field_key: '', label: '', description: '' }]);
  }
  function updateField(i: number, patch: Partial<FieldDraft>) {
    setFields((f) => f.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));
  }
  function removeField(i: number) {
    setFields((f) => f.filter((_, idx) => idx !== i));
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={mode === 'create' ? 'New document type' : `Edit ${existing!.name}`}
      size="lg"
      footer={
        <>
          <Btn variant="ghost" onClick={onClose}>
            Cancel
          </Btn>
          <Btn onClick={() => save.mutate()} loading={save.isPending} disabled={!name.trim()}>
            {mode === 'create' ? 'Create' : 'Save'}
          </Btn>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Name" required>
          <TextInput value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Public Liability Certificate" />
        </Field>
        <Field label="Description">
          <TextArea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <Field label={`Auto-pass threshold — ${threshold}%`} hint="At or above this overall confidence the upload passes automatically; below it escalates to manual review.">
          <input
            type="range"
            min={50}
            max={100}
            step={5}
            value={threshold}
            onChange={(e) => setThreshold(Number(e.target.value))}
            className="w-full"
          />
        </Field>

        <div>
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-medium text-slate-700">Fields to verify</span>
            <Btn size="sm" variant="secondary" onClick={addField}>
              + Add field
            </Btn>
          </div>
          {fields.length === 0 ? (
            <p className="text-xs italic text-slate-500">
              No fields yet. Add the fields the AI should confirm exist in the document (e.g.
              Insured name, Expiry date, Policy number).
            </p>
          ) : (
            <div className="space-y-2">
              {fields.map((f, i) => (
                <div key={i} className="flex items-start gap-2 rounded-lg border border-slate-200 p-2">
                  <div className="grid flex-1 grid-cols-2 gap-2">
                    <TextInput
                      value={f.label}
                      placeholder="Field label"
                      onChange={(e) =>
                        updateField(i, {
                          label: e.target.value,
                          field_key: f.field_key || slugify(e.target.value),
                        })
                      }
                    />
                    <TextInput
                      value={f.field_key}
                      placeholder="field_key"
                      onChange={(e) => updateField(i, { field_key: slugify(e.target.value) })}
                    />
                  </div>
                  <button
                    className="px-2 py-2 text-xs text-red-600 hover:underline"
                    onClick={() => removeField(i)}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
          Active
        </label>
        <ErrorMessage error={error} />
      </div>
    </Modal>
  );
}
